/**
 * Reading a duel's POT: the climb, what it is worth, and where the rungs are.
 *
 * Pure and DOM-free, for `saying-lines.ts`' and `inventory-strip.ts`' stated reason — where a
 * rung sits and what a climb is worth are things to assert, and the overlay they are drawn in
 * cannot be laid out in jsdom at all.
 *
 * ## THIS FILE HAS NEVER HEARD OF CHICKEN, AND THAT IS THE WHOLE POINT
 *
 * A push-your-luck mini-game declares a `MiniGamePot` in `@chompdome/shared/minigames`, and the
 * overlay draws whatever the *running duel's* game declares — read through `miniGamePotOf` where
 * the registry lookup already happens (`GameConnection.duelView`), never by a branch on a game's
 * id. That is the same line the simulation draws with `projectileSpecOf` in the power-up
 * registry: the reader asks "does this thing have a pot" and never "is this chicken". So a
 * second push-your-luck game is one more file in that directory and no change here.
 *
 * The rungs are the sharp end of it. A `MiniGamePot` exposes `share(percent)` and nothing about
 * where its bonus steps up, so the marks on the dial are **derived from the curve itself**: a
 * rung is a percentage point where the pot's value *per point of climb* jumps. Deriving them is
 * what keeps this module ignorant — the alternative is importing three constants that belong to
 * one game, which is exactly the branch this seam exists to avoid. The walk that finds them now
 * lives in `@chompdome/shared/minigames` (`miniGamePotRungs`) because a second reader that may
 * not name a game arrived: the bot brain, which aims its grab at one.
 *
 * ## THE CLIMB IS THE SERVER'S AND THERE IS NO CLOCK HERE
 *
 * `DuelSnapshot.pot` is on every frame, live while the duel is collecting and frozen at the
 * settling tick once it reveals. So the number on screen is the room's own, correct across a
 * stall and across a reconnect, and a countdown on this side would be a second copy of a value
 * that is already here — `inventory-strip.ts`' argument, one panel along.
 *
 * ## THE BANDS ARE THE DECLARED RANGES AND NEVER THE DRAWN POINTS
 *
 * A pot declares two ranges its two hidden thresholds are drawn from — `armMinPercent` /
 * `armMaxPercent` and `bustMinPercent` / `bustMaxPercent` — and both are shared constants every
 * build already carries, identical for every duel in the game. So a bar may shade them, and
 * shading them gives away nothing: "a grab anywhere under 50 may be early" is the same sentence
 * for every duel that has ever been played. What this duel actually rolled is drawn from the
 * room's seeded stream, rides `MiniGameRoundSecrets` to the game's own resolver, is on no frame
 * and in neither codec, and `DuelPot` has nowhere to put it — shading the *real* arm point would
 * hand a player the one answer that makes the penalty band stop existing.
 *
 * That is `bustMinPercent`'s rule exactly, which this module has read since the pot dial landed;
 * the arm range is read the same way, one field along, and for the same reason.
 *
 * ## THE RULER IS DERIVED FROM THE WIDTH, NOT HAND-PLACED
 *
 * `potScaleTicks` is the scale under the track: it picks the finest interval whose labels still
 * fit the width the bar was actually given, so 0/25/50/75/100 on the width every real surface
 * hands it and 0/50/100 on one narrow enough that five numbers would be a wall of type. Five
 * literals typed into the markup would be right on a desktop and wrong on the phone this is
 * played on, which is the same failure `hudZoneClass` derives its caps away from.
 *
 * The scale deliberately carries **no mark of its own**. The bust floor is a line on the track
 * and the ruler's business is to say what number that line is standing on; a tick in the scale
 * row at the same point would be a second drawing of one fact. See `DuelPotBar.tsx`.
 *
 * ## WHAT IS NOT HERE, AND MUST NEVER BE
 *
 * The tick the pot bursts on, and the tick it arms on. See above: both are drawn per duel from
 * the room's shared generator and neither reaches this process.
 */

import {
  MINI_GAME_POT_MAX_PERCENT,
  type MiniGamePot,
  miniGamePotRungs,
} from '@chompdome/shared/minigames';
import type { DuelPot } from '../net/connection';

/** One step up in the pot's bonus, derived from the curve rather than declared by a game. */
export interface DuelPotRung {
  /** The first whole percentage point at which this bonus applies. */
  percent: number;
  /** What a whole pot's worth of climb is multiplied by from here on. */
  multiplier: number;
  /** Whether the climb has reached it yet. */
  reached: boolean;
}

/**
 * One of the pot's two declared risk ranges, as a stretch of the bar.
 *
 * A *range* rather than a point, which is the whole of why it is safe to draw: these two numbers
 * are the same in every build and for every duel, so a band is a statement about the game and
 * never about this pot. See the header.
 */
export interface DuelPotBand {
  /** The first percentage point the band covers. */
  fromPercent: number;
  /** The last percentage point it covers. */
  toPercent: number;
}

/** Everything the overlay draws about a pot, from one place. */
export interface DuelPotReadout {
  /** How high the climb is, 0..100, straight off the wire. */
  percent: number;
  /** What grabbing right now is worth, as a multiple of the duel's ordinary stake. */
  multiplier: number;
  /** The bonus steps, in climb order. */
  rungs: readonly DuelPotRung[];
  /** The next one worth holding for, or null once the last has been passed. */
  nextRung: DuelPotRung | null;
  /** The lowest point this game's pot may ever burst at. Not this pot's point — see the header. */
  dangerFromPercent: number;
  /** Whether the climb is inside the range a bust can happen in. */
  inDanger: boolean;
  /**
   * The stretch of the bar in which a grab may be **early**, or null for a pot with no such band.
   *
   * Null rather than a zero-width band, because "this game punishes grabbing too soon" and "this
   * game does not" are different statements and a bar that drew a band of no width would be
   * making the second one look like the first. A pot whose declared arm range is empty is the
   * degenerate case a real game is measured against.
   */
  penaltyBand: DuelPotBand | null;
  /** The stretch in which a bust may happen. Always present: a pot that cannot burst is not one. */
  bustBand: DuelPotBand;
  /** What an early grab costs its grabber, as a multiple of the duel's ordinary stake. */
  earlyGrabPenalty: number;
  /** Whether the climb is still low enough that a grab might be early. */
  mayBeEarly: boolean;
}

/**
 * Where this pot's bonus steps up, in climb order.
 *
 * **THE WALK ITSELF MOVED TO `@chompdome/shared/minigames` AND THIS DELEGATES TO IT**, because it
 * gained a second reader: the bot brain aims its grab at a rung (`packages/game-core/src/bot.ts`)
 * and, like this module, may not name a game. Two copies of a derivation that has to agree with
 * `share()` is two things to get wrong. What is kept here is the name this overlay reads it by
 * and the shape it draws with, so nothing in the HUD learned that a bot exists.
 *
 * The identity of the returned table is the shared cache's, so asking twice still answers the
 * same array — which is what keeps the walk off the ten renders a chicken duel makes per second.
 */
export function duelPotRungs(pot: MiniGamePot): readonly Omit<DuelPotRung, 'reached'>[] {
  return miniGamePotRungs(pot);
}

/**
 * Everything about the running pot, or null when this duel does not have one.
 *
 * Null is still the ordinary answer: rock-paper-scissors declares no pot, and a game this build
 * has never heard of resolves to no pot either — both arrive here as `null` and both draw the
 * overlay that rock-paper-scissors has always had. Chicken is the one registered game that
 * answers otherwise.
 */
export function readDuelPot(pot: DuelPot | null): DuelPotReadout | null {
  if (pot === null) return null;
  const { spec } = pot;
  const percent = clampPercent(pot.percent);
  const rungs = duelPotRungs(spec).map((rung) => ({ ...rung, reached: percent >= rung.percent }));

  // Both bands are the game's DECLARED ranges, read exactly as `bustMinPercent` already is —
  // never `import { CHICKEN_ARM_MAX_PERCENT }`, which is precisely the branch this module's
  // header exists to refuse.
  const penaltyBand =
    spec.armMaxPercent > spec.armMinPercent
      ? { fromPercent: spec.armMinPercent, toPercent: spec.armMaxPercent }
      : null;

  return {
    percent,
    multiplier: spec.share(percent),
    rungs,
    nextRung: rungs.find((rung) => !rung.reached) ?? null,
    dangerFromPercent: spec.bustMinPercent,
    inDanger: percent >= spec.bustMinPercent,
    penaltyBand,
    bustBand: { fromPercent: spec.bustMinPercent, toPercent: spec.bustMaxPercent },
    earlyGrabPenalty: spec.earlyGrabPenalty,
    mayBeEarly: penaltyBand !== null && percent < penaltyBand.toPercent,
  };
}

/**
 * The intervals the ruler may be drawn at, finest first.
 *
 * Two rungs rather than a continuum: a scale is read at a glance, and 0/25/50/75/100 and
 * 0/50/100 are the two a person already has an intuition for. Anything between them (20, 10)
 * buys precision nobody is reading a ten-second bar for, and anything coarser than 50 stops
 * being a scale.
 */
const POT_SCALE_INTERVALS = [25, 50] as const;

/**
 * What one label needs of the track, in CSS pixels: the widest of them plus a gutter.
 *
 * "100%" is four characters of 9px bold tabular type — about 22 pixels — and a ruler whose
 * numbers touch is one nobody can pick a number out of, so the rest is the gap. It is a budget
 * rather than a measurement of the glyphs, which is what keeps this a pure function.
 */
export const POT_SCALE_LABEL_PX = 34;

/**
 * The percentages the scale under the track prints, at the finest interval that fits.
 *
 * **A width of 0 means "not laid out yet", and answers the coarse scale**, which is the safe way
 * round: `anchor.ts`' `isMeasured` rule, one panel along — a rectangle of zeroes must produce the
 * modest fallback rather than the maximal thing, because the frame before a measurement exists
 * is on screen. jsdom is the other caller that never measures anything.
 *
 * The finest interval wins when a whole step of it is at least `POT_SCALE_LABEL_PX` wide, so the
 * duel card (a `max-w-md` panel, ~416px of track on every surface it is drawn on, landscape phone
 * included) gets all five numbers and a window dragged under about 136px of track gets three.
 */
export function potScaleTicks(trackWidthPx: number): readonly number[] {
  const step =
    POT_SCALE_INTERVALS.find(
      (interval) => (trackWidthPx * interval) / MINI_GAME_POT_MAX_PERCENT >= POT_SCALE_LABEL_PX,
    ) ?? POT_SCALE_INTERVALS[POT_SCALE_INTERVALS.length - 1];

  const ticks: number[] = [];
  for (let percent = 0; percent <= MINI_GAME_POT_MAX_PERCENT; percent += step) ticks.push(percent);
  return ticks;
}

/**
 * The multiplier, for a person.
 *
 * Two places, always — a pot worth 1.5x and one worth 1.05x are a whole rung apart and "1.5x"
 * against "1.1x" hides which. Rounded for reading rather than for arithmetic, which is what
 * every other number in the HUD gets.
 */
export function formatPotMultiplier(multiplier: number): string {
  return `${multiplier.toFixed(2)}x`;
}

function clampPercent(percent: number): number {
  if (!(percent > 0)) return 0;
  const whole = Math.floor(percent);
  return whole > MINI_GAME_POT_MAX_PERCENT ? MINI_GAME_POT_MAX_PERCENT : whole;
}
